"use client"

import {ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon} from "lucide-react"
import React from "react"
import type {DayButton} from "react-day-picker"
import {DayPicker, getDefaultClassNames} from "react-day-picker"

import {Button, buttonVariants} from "@/components/ui/button"
import {cn} from "@/lib/cn"

/**
 * DIZED calendar on react-day-picker: the selected day uses the `action`
 * gradient (the portal's one active-state colour) rather than shadcn's
 * primary, day cells use `rounded-mini`, and the month caption matches every
 * other heading in the portal (`font-display uppercase tracking-wide`).
 *
 * Radix/react-day-picker, not a native element, because the OS date input
 * cannot be styled into the design language and cannot express a range —
 * see `Popover` for the same reasoning.
 */
export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) => {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar p-3 [--cell-size:--spacing(8)]",
        "[[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: date => date.toLocaleString("default", {month: "short"}),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({variant: buttonVariant}),
          "size-(--cell-size) rounded-mini p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({variant: buttonVariant}),
          "size-(--cell-size) rounded-mini p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "relative rounded-mini border border-[color:var(--card-border)] shadow-xs",
          "has-focus:border-transparent has-focus:ring-2 has-focus:ring-ring",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn("absolute inset-0 bg-popover opacity-0", defaultClassNames.dropdown),
        caption_label: cn(
          "select-none font-display font-bold uppercase tracking-wide",
          captionLayout === "label"
            ? "text-sm"
            : "flex h-8 items-center gap-1 rounded-mini pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
          defaultClassNames.caption_label,
        ),
        month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "flex-1 select-none rounded-mini text-[0.8rem] font-normal text-muted-foreground",
          defaultClassNames.weekday,
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn("w-(--cell-size) select-none", defaultClassNames.week_number_header),
        week_number: cn(
          "select-none text-[0.8rem] text-muted-foreground",
          defaultClassNames.week_number,
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center",
          "[&:last-child[data-selected=true]_button]:rounded-r-mini",
          props.showWeekNumber
            ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-mini"
            : "[&:first-child[data-selected=true]_button]:rounded-l-mini",
          defaultClassNames.day,
        ),
        range_start: cn(
          "rounded-l-mini bg-action text-action-foreground",
          defaultClassNames.range_start,
        ),
        range_middle: cn(
          "rounded-none bg-action/20 text-foreground",
          defaultClassNames.range_middle,
        ),
        range_end: cn(
          "rounded-r-mini bg-action text-action-foreground",
          defaultClassNames.range_end,
        ),
        today: cn(
          "rounded-mini bg-action/15 text-foreground data-[selected=true]:rounded-none",
          defaultClassNames.today,
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside,
        ),
        disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({className: rootClassName, rootRef, ...rootProps}) => (
          <div data-slot="calendar" ref={rootRef} className={cn(rootClassName)} {...rootProps} />
        ),
        Chevron: ({className: chevronClassName, orientation, ...chevronProps}) => {
          if (orientation === "left") {
            return <ChevronLeftIcon className={cn("size-4", chevronClassName)} {...chevronProps} />
          }
          if (orientation === "right") {
            return <ChevronRightIcon className={cn("size-4", chevronClassName)} {...chevronProps} />
          }
          return <ChevronDownIcon className={cn("size-4", chevronClassName)} {...chevronProps} />
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({children, ...weekNumberProps}) => (
          <td {...weekNumberProps}>
            <div className="flex size-(--cell-size) items-center justify-center text-center">
              {children}
            </div>
          </td>
        ),
        ...components,
      }}
      {...props}
    />
  )
}

const CalendarDayButton = ({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) => {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 rounded-mini",
        "font-normal leading-none",
        "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10",
        "group-data-[focused=true]/day:ring-2 group-data-[focused=true]/day:ring-ring",
        "data-[range-end=true]:rounded-r-mini data-[range-end=true]:bg-action data-[range-end=true]:text-action-foreground",
        "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-action/20 data-[range-middle=true]:text-foreground",
        "data-[range-start=true]:rounded-l-mini data-[range-start=true]:bg-action data-[range-start=true]:text-action-foreground",
        "data-[selected-single=true]:bg-action data-[selected-single=true]:text-action-foreground",
        "[&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}
